import Header from '@editorjs/header';
import ImageTool from '@editorjs/image';
import EditorJS, { BlockToolConstructable, OutputData } from '@editorjs/editorjs';
import EditorjsList from '@editorjs/list';
import EditorjsTable from '@editorjs/table';
import EditorjsWarning from '@editorjs/warning';
import EditorjsDelimiter from '@editorjs/delimiter';
import EditorjsAttaches from '@editorjs/attaches';
import { FileRefFragment } from '../generated/graphql';
import { Box, Stack } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface DocumentData {
  title: string;
  content: unknown;
}

interface DocumentEditorProps {
  document: DocumentData;
  updateLoading: boolean;
  disabled: boolean;

  onChange: (data: DocumentData) => void;

  addFileAttachment: (file: File) => Promise<FileRefFragment | undefined>;
  addUrlAttachment: (url: string) => Promise<FileRefFragment | undefined>;
}

export function DocumentEditor(props: DocumentEditorProps) {
  const { document, updateLoading, disabled } = props;

  const data = useMemo(() => document.content as OutputData, [document.content]);
  const [contentData, setContentData] = useState<OutputData>(data);

  const handleSave = (data: OutputData) => {
    props.onChange({
      title: document.title || '',
      content: data,
    });
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Editor
          data={data}
          onChange={(data) => {
            setContentData(data);
            handleSave(data);
          }}
          addAttachmentByFile={props.addFileAttachment}
          addAttachmentByUrl={props.addUrlAttachment}
        />
      </Box>
    </Stack>
  );
}

interface EditorProps {
  data: OutputData;
  onChange: (data: OutputData) => void;
  addAttachmentByFile: (file: File) => Promise<FileRefFragment | undefined>;
  addAttachmentByUrl: (url: string) => Promise<FileRefFragment | undefined>;
  onRemoveAttachment?: (fileId: string) => Promise<FileRefFragment | undefined>;
}

const Editor = (props: EditorProps) => {
  const { data } = props;
  const editorRef = useRef<EditorJS | null>(null);

  useEffect(() => {
    const composeResponse = (fileRef: FileRefFragment | undefined) => {
      if (!fileRef) {
        return {
          success: 0,
        };
      }
      return {
        success: 1,
        file: {
          fileRefId: fileRef.id,
          url: fileRef.urlMd, // Return the URL of the uploaded file
          urlXs: fileRef.urlXs,
          urlOriginal: fileRef.url,
          fileSize: fileRef.size,
          mimeType: fileRef.mimeType,
          name: fileRef.name,
          extension: fileRef.name?.split('.').pop() || '',
        },
      };
    };
    if (!editorRef.current) {
      const e = new EditorJS({
        holder: 'editorjs',
        onChange: async (inEditor, events) => {
          const content = await e.save();
          props.onChange(content);
        },
        onReady: () => {
          editorRef.current = e;
        },
        autofocus: true,
        data,

        tools: {
          header: Header,
          image: {
            class: ImageTool,
            config: {
              uploader: {
                uploadByFile: async (file: File) => {
                  const fileRef = await props.addAttachmentByFile(file);
                  return composeResponse(fileRef);
                },
                uploadByUrl: async (url: string) => {
                  const fileRef = await props.addAttachmentByUrl(url);
                  return composeResponse(fileRef);
                },
              },
            },
          },
          attaches: {
            class: EditorjsAttaches,
            config: {
              uploader: {
                uploadByFile: async (file: File) => {
                  const fileRef = await props.addAttachmentByFile(file);
                  return composeResponse(fileRef);
                },
              },
            },
          },
          list: {
            class: EditorjsList,
            inlineToolbar: true,
            config: {
              defaultStyle: 'unordered',
            },
          },
          table: {
            // FIXME: Typing issue with EditorjsTable. Need to cast to BlockToolConstructable.
            class: EditorjsTable as BlockToolConstructable,
            inlineToolbar: true,
          },
          warning: {
            class: EditorjsWarning,
            inlineToolbar: true,
          },
          delimiter: EditorjsDelimiter,
        },
      });
    }
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, []);

  return <div id="editorjs"></div>;
};
